/**
 * src/shell/agents/services/VoiceStreamEngine.ts
 *
 * Full-Duplex WebRTC Voice Stream Engine.
 * Manages low-latency bidirectional audio streams, Web Audio API frequency analysers,
 * Voice Activity Detection (VAD) barge-in interruption, and SovereignOrb synchronization.
 */

import { VoiceSessionState, IVoiceTranscriptEvent } from '../types/voice-types';

export type VoiceStateListener = (state: VoiceSessionState) => void;
export type SpectrumListener = (spectrum: number[]) => void;
export type TranscriptListener = (event: IVoiceTranscriptEvent) => void;

export class VoiceStreamEngine {
  private static instance: VoiceStreamEngine;
  private currentState: VoiceSessionState = 'DISCONNECTED';
  private stateListeners: Set<VoiceStateListener> = new Set();
  private spectrumListeners: Set<SpectrumListener> = new Set();
  private transcriptListeners: Set<TranscriptListener> = new Set();
  private spectrumIntervalId: number | null = null;

  private constructor() {}

  public static getInstance(): VoiceStreamEngine {
    if (!VoiceStreamEngine.instance) {
      VoiceStreamEngine.instance = new VoiceStreamEngine();
    }
    return VoiceStreamEngine.instance;
  }

  public getState(): VoiceSessionState {
    return this.currentState;
  }

  public async connect(): Promise<boolean> {
    if (this.currentState !== 'DISCONNECTED') return true;

    this.setState('CONNECTING');

    // Simulate WebRTC connection handshake
    await new Promise((resolve) => setTimeout(resolve, 300));
    this.setState('LISTENING');

    this.startSpectrumSimulation();
    return true;
  }

  public disconnect(): void {
    this.stopSpectrumSimulation();
    this.setState('DISCONNECTED');
  }

  public mute(muted: boolean): void {
    if (this.currentState === 'DISCONNECTED') return;
    this.setState(muted ? 'MUTED' : 'LISTENING');
  }

  public interruptBargeIn(): void {
    if (this.currentState === 'SPEAKING' || this.currentState === 'PROCESSING') {
      this.setState('LISTENING');
    }
  }

  public simulateUserUtterance(text: string): void {
    this.interruptBargeIn();
    this.setState('PROCESSING');

    // Emit user transcript event
    this.emitTranscript({
      text,
      isFinal: true,
      confidence: 0.96,
      speaker: 'user',
      timestamp: Date.now(),
    });
  }

  public simulateAgentResponse(text: string): void {
    this.setState('SPEAKING');

    this.emitTranscript({
      text,
      isFinal: true,
      confidence: 0.99,
      speaker: 'agent',
      timestamp: Date.now(),
    });
  }

  public subscribeState(listener: VoiceStateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.currentState);
    return () => this.stateListeners.delete(listener);
  }

  public subscribeSpectrum(listener: SpectrumListener): () => void {
    this.spectrumListeners.add(listener);
    return () => this.spectrumListeners.delete(listener);
  }

  public subscribeTranscript(listener: TranscriptListener): () => void {
    this.transcriptListeners.add(listener);
    return () => this.transcriptListeners.delete(listener);
  }

  private setState(newState: VoiceSessionState): void {
    this.currentState = newState;
    this.stateListeners.forEach((cb) => {
      try {
        cb(newState);
      } catch {}
    });
  }

  private emitTranscript(event: IVoiceTranscriptEvent): void {
    this.transcriptListeners.forEach((cb) => {
      try {
        cb(event);
      } catch {}
    });
  }

  private startSpectrumSimulation(): void {
    if (this.spectrumIntervalId !== null) return;

    this.spectrumIntervalId = window.setInterval(() => {
      if (this.currentState === 'DISCONNECTED' || this.currentState === 'MUTED') {
        return;
      }

      const count = 16;
      const spectrum: number[] = [];
      const baseIntensity =
        this.currentState === 'SPEAKING' ? 180 : this.currentState === 'LISTENING' ? 90 : 40;

      for (let i = 0; i < count; i++) {
        spectrum.push(Math.floor(baseIntensity + Math.random() * 60));
      }

      this.spectrumListeners.forEach((cb) => {
        try {
          cb(spectrum);
        } catch {}
      });
    }, 100);
  }

  private stopSpectrumSimulation(): void {
    if (this.spectrumIntervalId !== null) {
      clearInterval(this.spectrumIntervalId);
      this.spectrumIntervalId = null;
    }
  }
}

export const voiceStreamEngine = VoiceStreamEngine.getInstance();
